import { useAuth } from '../../auth/useAuth';
import { Button } from '../ui/Button';
import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, LogOut } from 'lucide-react';
import { fetchGlobalSearch, type GlobalSearchResult } from '../../lib/bridge';

// Global search (docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md §8) — reuses
// the existing appGlobalSearch(query) unmodified. Debounced, dropdown of
// customer/job matches, click or Enter (first result) navigates there.
function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchMutation = useMutation({
    mutationFn: (q: string) => fetchGlobalSearch(q),
    onSuccess: (result) => setResults(result.results ?? []),
  });

  function goTo(result: GlobalSearchResult) {
    setQuery('');
    setResults([]);
    navigate(result.type === 'customer' ? `/customers/${encodeURIComponent(result.id)}` : `/jobs/${encodeURIComponent(result.id)}`);
  }

  return (
    <div className="relative hidden w-full max-w-xs sm:block">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" aria-hidden="true" />
      <input
        aria-label="Search"
        placeholder="Search customers or jobs…"
        className="w-full rounded-lg border border-ink-border bg-ink px-3 py-2 pl-9 text-sm text-white placeholder:text-slate-600 outline-none transition focus:border-gold-700 focus:ring-2 focus:ring-gold-900/60"
        value={query}
        onChange={(e) => {
          const value = e.target.value;
          setQuery(value);
          if (debounceRef.current) clearTimeout(debounceRef.current);
          if (value.trim().length < 2) {
            setResults([]);
            return;
          }
          debounceRef.current = setTimeout(() => searchMutation.mutate(value), 250);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && results.length > 0) goTo(results[0]);
        }}
      />
      {results.length > 0 && (
        <ul className="absolute z-30 mt-1.5 w-full overflow-hidden rounded-xl border border-ink-border bg-ink-raised shadow-card">
          {results.map((r) => (
            <li key={`${r.type}-${r.id}`}>
              <button type="button" onClick={() => goTo(r)} className="block w-full px-3 py-2.5 text-left text-sm hover:bg-white/5">
                <span className="font-semibold text-white">{r.title}</span>
                {r.subtitle && <span className="ml-1.5 text-xs text-slate-500">{r.subtitle}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function initialsOf(name: string | undefined) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}

export function TopBar() {
  const { user, logout } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center gap-4 border-b border-ink-border bg-ink/90 px-4 backdrop-blur-lg tablet:px-6">
      <div className="flex items-center gap-2 tablet:hidden">
        <img src="/ps-logo.webp" alt="" className="h-8 w-8 rounded-full object-contain" />
      </div>
      <div className="flex-1">{user && <GlobalSearch />}</div>
      <div className="flex shrink-0 items-center gap-3">
        {user && (
          <div className="hidden items-center gap-2.5 sm:flex">
            <span
              className="grid h-9 w-9 place-items-center rounded-full border border-gold-700 bg-gradient-to-br from-[#2b2414] to-[#16120b] text-xs font-extrabold text-gold-200"
              aria-hidden="true"
            >
              {initialsOf(user.name)}
            </span>
            <span className="flex flex-col leading-tight">
              <span className="text-xs font-semibold text-white">{user.name}</span>
              <span className="text-[10px] text-slate-500">{user.role.replace(/_/g, ' ')}</span>
            </span>
          </div>
        )}
        <Button
          variant="ghost"
          busy={loggingOut}
          busyLabel="Signing out…"
          className="!px-2.5"
          onClick={async () => {
            setLoggingOut(true);
            try {
              await logout();
            } finally {
              setLoggingOut(false);
            }
          }}
        >
          <LogOut className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Sign out</span>
        </Button>
      </div>
    </header>
  );
}
